import { requireCompletedProfile } from "@hisani/auth";
import { browseJobPostingsSchema, closeJobPostingSchema, createJobPostingSchema, getJobPostingSchema, listJobPostingsForStableSchema, reopenJobPostingSchema, stableConflict, stableNotFound, updateJobPostingSchema, } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
import { jobPostingResponse, publicJobPostingDetailResponse, publicJobPostingSummaryResponse, } from "./hire-response-projections.js";
const FEATURE = "HIRE";
function postingMutationResult(result) {
    switch (result.outcome) {
        case "UPDATED":
        case "REPLAYED":
            return jobPostingResponse(result.posting);
        case "NOT_FOUND":
            throw stableNotFound();
        case "STALE_VERSION":
            throw stableConflict("This job posting has changed. Refresh and retry.");
        case "IDEMPOTENCY_CONFLICT":
            throw stableConflict("This mutation identifier was already used for a different Hire command.");
    }
}
export async function createJobPosting(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = createJobPostingSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const result = await dependencies.repository.createJobPosting({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        createdByHorsemanId: actor.account.id,
        title: parsed.fields.title,
        roleSought: parsed.fields.roleSought,
        description: parsed.fields.description,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint(JSON.stringify([
            "HIRE_CREATE_POSTING",
            parsed.stableId,
            parsed.fields.title,
            parsed.fields.roleSought,
            parsed.fields.description,
        ])),
        now: dependencies.clock.now(),
    });
    if (result.outcome === "IDEMPOTENCY_CONFLICT") {
        throw stableConflict("This mutation identifier was already used for a different Hire command.");
    }
    return jobPostingResponse(result.posting);
}
export async function updateJobPosting(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = updateJobPostingSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const result = await dependencies.repository.updateJobPosting({
        stableId: parsed.stableId,
        postingId: parsed.postingId,
        title: parsed.fields.title,
        roleSought: parsed.fields.roleSought,
        description: parsed.fields.description,
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint(JSON.stringify([
            "HIRE_UPDATE_POSTING",
            parsed.stableId,
            parsed.postingId,
            parsed.expectedVersion,
            parsed.fields.title,
            parsed.fields.roleSought,
            parsed.fields.description,
        ])),
        expectedVersion: parsed.expectedVersion,
        now: dependencies.clock.now(),
    });
    return postingMutationResult(result);
}
export async function closeJobPosting(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = closeJobPostingSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const result = await dependencies.repository.closeJobPosting({
        stableId: parsed.stableId,
        postingId: parsed.postingId,
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint(JSON.stringify([
            "HIRE_CLOSE_POSTING",
            parsed.stableId,
            parsed.postingId,
            parsed.expectedVersion,
        ])),
        expectedVersion: parsed.expectedVersion,
        now: dependencies.clock.now(),
    });
    return postingMutationResult(result);
}
export async function reopenJobPosting(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = reopenJobPostingSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "USE");
    const result = await dependencies.repository.reopenJobPosting({
        stableId: parsed.stableId,
        postingId: parsed.postingId,
        actorId: actor.account.id,
        clientMutationId: parsed.clientMutationId,
        commandFingerprint: dependencies.identifierFingerprint.fingerprint(JSON.stringify([
            "HIRE_REOPEN_POSTING",
            parsed.stableId,
            parsed.postingId,
            parsed.expectedVersion,
        ])),
        expectedVersion: parsed.expectedVersion,
        now: dependencies.clock.now(),
    });
    return postingMutationResult(result);
}
export async function listJobPostingsForStable(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listJobPostingsForStableSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    const postings = await dependencies.repository.listJobPostingsForStable(parsed.stableId);
    return postings.map(jobPostingResponse);
}
/** Public, anonymous-safe browse — mirrors `browseMarketplace`: never takes
 * a principal, never touches `stable_memberships`
 * (`COMPANY.md` "Anonymous Browsing"). */
export async function browseJobPostings(input, dependencies) {
    const parsed = browseJobPostingsSchema.parse(input);
    const result = await dependencies.repository.browseJobPostings(parsed);
    return {
        postings: result.postings.map(publicJobPostingSummaryResponse),
        nextCursor: result.nextCursor,
    };
}
/** Public, anonymous-safe detail read — resolves regardless of posting
 * status so a direct link still works, mirroring `getMarketplaceListing`'s
 * unauthenticated contract. */
export async function getJobPosting(postingId, dependencies) {
    const parsed = getJobPostingSchema.parse({ postingId });
    const posting = await dependencies.repository.getJobPosting(parsed.postingId);
    if (posting === null)
        throw stableNotFound();
    return publicJobPostingDetailResponse(posting);
}
